<template>
  <div class="flex flex-col gap-2.5">
    <h2 class="text-base font-semibold text-center">Setup Your School's Calendar</h2>
    <div class="text-xs text-gray-600 text-center">
      First, let's define your school's session structure. This determines how an academic year is divided (e.g., into
      terms or semesters).
    </div>

    <div v-if="loading || loadingSchool" class="flex justify-center p-2.5">
      <i class="pi pi-spin pi-spinner text-lg text-primary"></i>
    </div>

    <div v-else-if="!showCustomForm" class="flex flex-col gap-2.5 mt-2">
      <div @click="useStandard"
        class="border-2 border-primary rounded-lg p-2.5 text-center cursor-pointer hover:bg-primary/10 transition">
        <h3 class="font-bold text-primary text-sm">{{ standardOption.title }}</h3>
        <p class="text-xs text-gray-500 mt-1">{{ standardOption.description }}</p>
      </div>

      <div @click="initCustomForm"
        class="border border-gray-300 rounded-lg p-2.5 text-center cursor-pointer hover:bg-gray-100 transition">
        <h3 class="font-semibold text-gray-700 text-sm">Custom Setup</h3>
        <p class="text-xs text-gray-500 mt-1">Manually define your session structure and terms.</p>
      </div>
    </div>

    <!-- Manual Form -->
    <div v-else class="mt-1.5 flex flex-col gap-2.5">
      <div class="flex justify-between items-center border-b pb-1.5">
        <h3 class="font-semibold text-gray-800 text-sm">Custom Structure</h3>
        <Button label="Back" icon="pi pi-arrow-left" class="p-button-text p-button-sm"
          @click="showCustomForm = false" />
      </div>

      <div class="flex flex-col gap-1.5">
        <label class="text-xs font-medium">Structure Name</label>
        <InputText v-model="customForm.name" placeholder="e.g. Trimester System" />
      </div>

      <div class="flex flex-col gap-1.5">
        <label class="text-xs font-medium">Description</label>
        <InputText v-model="customForm.description" placeholder="e.g. 3 terms per session" />
      </div>

      <div class="space-y-1.5">
        <label class="text-xs font-medium">Terms / Semesters</label>
        <div v-for="(term, index) in customForm.terms" :key="index" class="flex gap-1.5 items-center">
          <span class="text-xs text-gray-500 w-6 text-center">{{ index + 1 }}.</span>
          <InputText v-model="term.name" placeholder="Term Name" class="flex-1 p-inputtext-sm" />
          <Button icon="pi pi-trash" class="p-button-rounded p-button-text p-button-danger p-button-sm"
            @click="removeTerm(index)" :disabled="customForm.terms.length <= 1" />
        </div>
        <Button label="Add Term/Semester" icon="pi pi-plus" class="p-button-outlined p-button-sm w-full mt-1.5"
          @click="addTerm" />
      </div>

      <Button label="Save Custom Structure" class="w-full mt-2 !bg-primary hover:!bg-primary/90 !border-primary"
        size="small" @click="submitCustom" :loading="submitting" :disabled="!isValid" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { useUserStore } from '@/store/userStore';
import { SessionStructure, TermStructure } from '@/constants/session-structure.constant';
import { useToast } from 'primevue/usetoast';
import { useSchoolQuery } from '@/composables/queries/useSchoolQuery';
import { useSchoolMutations } from '@/composables/mutations/school/useSchoolMutations';

const emit = defineEmits(['success']);
const userStore = useUserStore();
const toast = useToast();
const { createSessionStructure, createTermsStructure } = useSchoolMutations();

const { school, loadingSchool } = useSchoolQuery();

const loading = ref(false);
const submitting = ref(false);
const showCustomForm = ref(false);

const schoolType = computed(() => (school.value?.school_type || 'secondary').toLowerCase() as keyof typeof SessionStructure);

const standardOption = computed(() => {
  const structure = SessionStructure[schoolType.value];
  const terms = TermStructure[schoolType.value];

  if (!structure || !terms) {
    return {
      title: 'Use Standard Structure',
      description: 'Standard session structure.',
    };
  }

  // Create a description listing the terms/semesters
  // e.g. "1st Term, 2nd Term & 3rd Term"
  const termNames = terms.map(t => t.name);
  let formattedTermNames = '';

  if (termNames.length > 1) {
    const last = termNames.pop();
    formattedTermNames = termNames.join(', ') + ' & ' + last;
  } else {
    formattedTermNames = termNames[0] || '';
  }

  return {
    title: `Use ${structure.name}`,
    description: `${structure.description} (${formattedTermNames}).`,
  };
});

// Custom Form Logic
const customForm = ref({
  name: '',
  description: '',
  terms: [] as { name: string, sequence: number }[],
});

function initCustomForm() {
  const isTertiary = schoolType.value === 'tertiary';
  customForm.value = {
    name: isTertiary ? 'Semester System' : 'Term System',
    description: '',
    terms: isTertiary
      ? [{ name: '1st Semester', sequence: 1 }, { name: '2nd Semester', sequence: 2 }]
      : [{ name: '1st Term', sequence: 1 }, { name: '2nd Term', sequence: 2 }, { name: '3rd Term', sequence: 3 }],
  };
  showCustomForm.value = true;
}

function addTerm() {
  const nextSeq = customForm.value.terms.length + 1;
  const isTertiary = schoolType.value === 'tertiary';
  const suffix = isTertiary ? 'Semester' : 'Term';

  // Simple ordinal helper (1st, 2nd, 3rd, 4th...)
  const getOrdinal = (n: number) => {
    const s = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
  };

  customForm.value.terms.push({
    name: `${getOrdinal(nextSeq)} ${suffix}`,
    sequence: nextSeq,
  });
}

function removeTerm(index: number) {
  customForm.value.terms.splice(index, 1);
  // Re-sequence
  customForm.value.terms.forEach((t, i) => t.sequence = i + 1);
}

const isValid = computed(() => customForm.value.name.trim() !== '' &&
    customForm.value.terms.length > 0 &&
    customForm.value.terms.every(t => t.name.trim() !== ''));

async function submitCustom() {
  submitting.value = true;
  try {
    // 1. Create Session Structure
    await createSessionStructure({
      name: customForm.value.name,
      description: customForm.value.description,
      is_default: true, // Assuming custom created here becomes default
    });

    const sessionStructureId = userStore.tempSessionStructureId;
    if (!sessionStructureId) {
      throw new Error('Failed to create session structure.');
    }

    // 2. Create Term Structures
    for (const term of customForm.value.terms) {
      await createTermsStructure({
        session_structure_id: sessionStructureId,
        name: term.name,
        sequence: term.sequence,
      });
    }

    toast.add({ severity: 'success', summary: 'Success', detail: 'Custom structure created successfully!', life: 3000 });
    emit('success');

  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Failed to create custom structure.';
    toast.add({ severity: 'error', summary: 'Error', detail: message, life: 3000 });
  } finally {
    submitting.value = false;
  }
}

async function useStandard() {
  loading.value = true;
  try {
    // 1. Create Session Structure
    const structurePayload = SessionStructure[schoolType.value];
    if (!structurePayload) {
      throw new Error('Invalid school type for session structure.');
    }
    await createSessionStructure(structurePayload);

    const sessionStructureId = userStore.tempSessionStructureId;
    if (!sessionStructureId) {
      throw new Error('Failed to create session structure.');
    }

    // 2. Create Term Structures
    const termsPayload = TermStructure[schoolType.value];
    for (const term of termsPayload) {
      await createTermsStructure({
        session_structure_id: sessionStructureId,
        name: term.name,
        sequence: term.sequence,
      });
    }

    toast.add({ severity: 'success', summary: 'Success', detail: 'Session structure created successfully!', life: 3000 });
    emit('success');

  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Failed to set up standard structure.';
    toast.add({ severity: 'error', summary: 'Error', detail: message, life: 3000 });
  } finally {
    loading.value = false;
  }
}
</script>
